import MomentUtils from '@date-io/moment';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import moment, { Moment } from 'moment-timezone';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useBackdrop from '../../../hooks/useBackdrop';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import { deleteGiftboxById, updateGiftboxStartDateById } from '../../../services/api/Giftbox';
import { IGiftbox } from '../../../services/types';
import { RouteNames } from '../../config';
import useStyles from './styles';

interface IProps {
  liveGiftboxes: IGiftbox[];
  scheduledGiftboxes: IGiftbox[];
  unscheduledGiftboxes: IGiftbox[];
  onReload: () => void;
  onSelect: (giftbox: IGiftbox) => void;
}

const GiftboxList = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const { liveGiftboxes, scheduledGiftboxes, unscheduledGiftboxes, onReload } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedGiftbox, setSelectedGiftbox] = useState<IGiftbox | null>(null);
  const [selectedDate, setSelectedDate] = useState<Moment>(moment());
  const [dateType, setDateType] = useState<number>(0); // 0 is Right now, otherwise is scheduled.
  const [showEditCard, setShowEditCard] = useState<boolean>(false);

  const openMenu = (event: any, giftbox: IGiftbox) => {
    setAnchorEl(event.currentTarget);
    setSelectedGiftbox(giftbox);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedGiftbox(null);
  };

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const handleSubmitClick = useCallback(async () => {
    if (!selectedGiftbox || selectedGiftbox.id === undefined) return;

    const onProceed = async () => {
      backdrop.show();
      try {
        await updateGiftboxStartDateById(selectedGiftbox.id, selectedDate.format());
        snackbar.success('Saved changes successfully.');
        onReload();
      } catch (e) {
        snackbar.error(`An error has occurred. ${e.message}`);
      } finally {
        backdrop.hide();
      }
    };

    if (dateType === 0) {
      confirm.open({
        onProceed,
      });
    } else {
      onProceed();
    }

    setShowEditCard(false);
  }, [confirm, dateType, selectedDate, selectedGiftbox, backdrop, onReload, snackbar]);

  const deleteGiftbox = useCallback(async () => {
    if (!selectedGiftbox || selectedGiftbox.id === undefined) return;

    const onProceed = async () => {
      backdrop.show();
      try {
        await deleteGiftboxById(selectedGiftbox.id);
        snackbar.info('Deleted successfully.');
        onReload();
      } catch (e) {
        snackbar.error(`An error has occurred. ${e.message}`);
      } finally {
        backdrop.hide();
      }
    };

    confirm.open({
      onProceed,
    });

    setAnchorEl(null);
  }, [selectedGiftbox, backdrop, onReload, confirm, snackbar]);

  useEffect(() => {
    if (dateType === 0) {
      setSelectedDate(moment());
    }
  }, [dateType]);

  useEffect(() => {
    if (showEditCard) {
      setAnchorEl(null);
    } else {
      onReload();
    }
  }, [showEditCard, onReload]);

  return (
    <div className={classes.listRoot}>
      <Paper className={classes.listPaper}>
        <Typography className={classes.listPaperTitle} variant="h6">
          Live Gift Box
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Started At</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liveGiftboxes &&
              liveGiftboxes.map(giftbox => (
                <TableRow
                  component={Link}
                  to={`${RouteNames.Giftbox}/v/${giftbox.id}`}
                  key={giftbox.id}
                  className={classes.storyTableRow}
                  onClick={() => {
                    if (!giftbox) {
                      return;
                    }

                    if (giftbox) {
                      props.onSelect(giftbox);
                    }
                  }}
                >
                  <TableCell>{giftbox.title}</TableCell>
                  <TableCell>{giftbox.startDate && moment(giftbox.startDate).format('llll')} ~ </TableCell>
                  <TableCell align="right"></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>
      <Paper className={classes.listPaper}>
        <Typography className={classes.listPaperTitle} variant="h6">
          Scheduled Gift Box
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Starts At</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scheduledGiftboxes &&
              scheduledGiftboxes.map(giftbox => (
                <TableRow
                  component={Link}
                  to={`${RouteNames.Giftbox}/v/${giftbox.id}`}
                  key={giftbox.id}
                  className={classes.storyTableRow}
                  onClick={() => {
                    if (!giftbox) {
                      return;
                    }

                    if (giftbox) {
                      props.onSelect(giftbox);
                    }
                  }}
                >
                  <TableCell>{giftbox.title}</TableCell>
                  <TableCell>{giftbox.startDate && moment(giftbox.startDate).format('llll')} ~ </TableCell>
                  <TableCell align="right">
                    <MoreVertIcon
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMenu(e, giftbox);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>
      <Paper className={classes.listPaper}>
        <Typography className={classes.listPaperTitle} variant="h6">
          Gift Box List
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {unscheduledGiftboxes &&
              unscheduledGiftboxes.map(giftbox => (
                <TableRow
                  component={Link}
                  to={`${RouteNames.Giftbox}/v/${giftbox.id}`}
                  key={giftbox.id}
                  className={classes.storyTableRow}
                  onClick={() => {
                    if (!giftbox) {
                      return;
                    }

                    if (giftbox) {
                      props.onSelect(giftbox);
                    }
                  }}
                >
                  <TableCell>{giftbox.title}</TableCell>
                  <TableCell>{giftbox.createdAt && moment(giftbox.createdAt).format('llll')}</TableCell>
                  <TableCell align="right">
                    <MoreVertIcon
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMenu(e, giftbox);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>
      <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={closeMenu}>
        <MenuItem
          onClick={() => {
            setShowEditCard(true);
          }}
        >
          Schedule Edit
        </MenuItem>
        {selectedGiftbox && liveGiftboxes.indexOf(selectedGiftbox) === -1 && <MenuItem onClick={deleteGiftbox}>Delete</MenuItem>}
      </Menu>
      {showEditCard && (
        <Card className={classes.editCard}>
          <CardContent className={classes.editCardContainer}>
            <Typography variant="h6">Live Gift Box</Typography>
            <Typography>{selectedGiftbox && selectedGiftbox.title}</Typography>
            <Grid container alignItems="center">
              <Grid item xs={3}>
                <Typography>Start</Typography>
              </Grid>
              <Grid item xs={9}>
                <FormControl component="fieldset">
                  <RadioGroup row value={dateType} onChange={e => setDateType(parseInt(e.target.value, 10) || 0)}>
                    <FormControlLabel value={0} label="Right Now" control={<Radio color="primary" />} />
                    <FormControlLabel value={1} label="Scheduled" control={<Radio color="primary" />} />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
            <Grid container alignItems="center">
              <Grid item xs={3}>
                <Typography>Scheduled Date</Typography>
              </Grid>
              <Grid item xs={9}>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <Grid container justify="space-around">
                    <KeyboardDateTimePicker
                      format="llll"
                      margin="normal"
                      id="date-picker-inline"
                      disabled={dateType === 0}
                      value={selectedDate}
                      minDate={moment().toDate()}
                      disablePast={true}
                      minDateMessage="Schedule time must be in the future."
                      onChange={e => {
                        if ((e as Moment).isBefore(moment())) {
                          alert('Schedule time must be in the future.');
                        } else {
                          setSelectedDate(e as Moment);
                        }
                      }}
                    />
                  </Grid>
                </MuiPickersUtilsProvider>
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end" className={classes.editCardButtonContainer}>
              <Button
                className={classes.cancelButton}
                onClick={() => {
                  setShowEditCard(false);
                }}
              >
                CANCEL
              </Button>
              <Button variant="contained" color="primary" onClick={handleSubmitClick}>
                SUBMIT
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GiftboxList;
